import { keymap } from 'prosemirror-keymap';
import type { Node as ProsemirrorNode } from 'prosemirror-model';
import { Fragment, Slice } from 'prosemirror-model';
import { Plugin } from 'prosemirror-state';
import type { EditorView } from 'prosemirror-view';
import type { Observable, Subscription } from 'rxjs';
import { Subject, withLatestFrom } from 'rxjs';
import { distinctUntilChanged, takeUntil } from 'rxjs/operators';

import type { EditorButton, EditorMode } from '../../editor/types';
import { AlignExtension } from '../../extensions/align';
import { nodeTypeSelectionObserver } from '../../helpers/node-type-selection-observer';
import { DEFAULT_KEYMAP_INPUTRULE_PRIORITY } from '../../helpers/utils';
import { NodeName } from '../../schema/nodes';
import type {
  FigureContextAction,
  FigureContextMenuController,
} from '../context-menu/figure-context.types';
import { VideoNode } from './nodes/video-node';
import type { VideoNodeAttrs, VideoSchema } from './nodes/video-node.types';
import { VideoPluginService } from './video-plugin.service';
import type {
  VideoEditMenuCallbacks,
  VideoEditMenuData,
  VideoMenuController,
  CreateVideoNodeView,
  VideoEditMenuResult,
  VideoParser,
} from './video-plugin.types';

export class VideoPlugin {
  public api = {
    videoService: VideoPluginService,
  };
  public buttons = {
    toggleVideo: <EditorButton>{
      icon: 'videocam',
      tooltipText: 'wysiwyg.video',
      isActive: () => !!this.menuController.editDialogRef,
      run: (state, dispatch, origin) => {
        this.menuController.openEdit(
          { src: '' },
          {
            cancel: () => this.menuController.closeEdit(),
            changeSource: data => this.changeSource(data),
          },
        );
      },
    },
  };

  private videoNodeSelection$ = new Subject<number | null>();
  private destroy$ = new Subject<void>();
  private editorView: EditorView;
  private contextActionsSubscription: Subscription | null = null;
  private pasteVideoPlugin = new Plugin({
    appendTransaction: nodeTypeSelectionObserver(this.videoNodeSelection$, NodeName.Video),
    props: {
      nodeViews: {
        [VideoNode.nodeName]: (node, view, getPos) => {
          const onResize = (newWidth: number, newHeight: number, position: number) => {
            this.api.videoService.updateWidth(
              position,
              newWidth,
              newHeight,
              this.editorView.state,
              this.editorView.dispatch,
            );
            this.repositionContextMenu(position);
          };

          return this.videoNodeViewFactory(node, view, getPos, {
            nodeType: this.editorView.state.schema.nodes[NodeName.Video]!,
            onResize,
            isEditable: true,
            videoNodeSelection$: this.videoNodeSelection$,
          });
        },
      },
      transformPasted: slice => {
        let newContent = Fragment.from();

        slice.content.forEach((node, offset, index) => {
          const nodeText = node.textBetween(0, node.content.size);
          const src = this.videoParserService.parse(nodeText);
          newContent = newContent.append(
            Fragment.from(src !== '' ? this.createVideoNode(node, src) : node),
          );
        });

        return new Slice(newContent, slice.openStart, slice.openEnd);
      },
    },
  });

  constructor(
    private videoNodeViewFactory: CreateVideoNodeView,
    private menuController: VideoMenuController,
    private contextMenuController: FigureContextMenuController,
    private videoParserService: VideoParser,
    protected scroll$: Observable<void>,
  ) {
    scroll$
      .pipe(withLatestFrom(this.videoNodeSelection$), takeUntil(this.destroy$))
      .subscribe(([_, position]) => {
        if (position) this.repositionContextMenu(position);
      });
    this.videoNodeSelection$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(position => this.updateContextMenu(position));
  }

  public destroy() {
    if (this.contextActionsSubscription) {
      this.contextActionsSubscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  public afterInit(mode: EditorMode, editorView: EditorView) {
    this.editorView = editorView;
  }

  public getNativeEditorPlugins(schema: VideoSchema) {
    return [
      this.pasteVideoPlugin,
      { priority: DEFAULT_KEYMAP_INPUTRULE_PRIORITY, plugin: keymap(this.getKeyBindings(schema)) },
    ];
  }

  private updateContextMenu(position: number | null) {
    this.contextMenuController.close();
    if (this.contextActionsSubscription) {
      this.contextActionsSubscription.unsubscribe();
      this.contextActionsSubscription = null;
    }
    if (position === null) return;
    const originRect = this.getContextMenuRect(position);
    if (!originRect) return;
    const { action$ } = this.contextMenuController.open(originRect, {
      getAlign: () => {
        const { state } = this.editorView;
        return AlignExtension.getAlignAtPosition(state, position);
      },
    });
    this.contextActionsSubscription = action$.subscribe(action =>
      this.onContextAction(action, position),
    );
  }

  private onContextAction(action: FigureContextAction, position: number) {
    switch (action.type) {
      case 'edit':
        this.openEditMenu(position);
        break;
      case 'delete':
        this.deleteVideoNode(position);
        break;
      case 'resetSize':
        this.api.videoService.resetSize(position, this.editorView.state, this.editorView.dispatch);
        this.repositionContextMenu(position);
        break;
      case 'align':
        const cmd = AlignExtension.apply(action.value);
        const { state, dispatch } = this.editorView;
        cmd(state, dispatch);
        this.repositionContextMenu(position);
        break;
      default:
        break;
    }
  }

  private openEditMenu(position: number) {
    const videoNode = this.editorView.state.doc.nodeAt(position);

    if (!videoNode?.attrs) return;

    const data = <VideoNodeAttrs>videoNode.attrs;
    const callbacks = {
      cancel: () => this.menuController.closeEdit(),
      delete: () => this.deleteVideoNode(position),
      changeSource: (data: VideoEditMenuResult) => this.changeSource(data, videoNode),
    };

    this.openEditDialog(data, callbacks);
  }

  private repositionContextMenu(position: number) {
    const originRect = this.getContextMenuRect(position);
    if (originRect) {
      this.contextMenuController.updatePosition(originRect);
    }
  }

  private getContextMenuRect(position: number): DOMRect | null {
    const nodeDOM = <HTMLElement | null>this.editorView.nodeDOM(position);
    if (!nodeDOM) return null;
    const figureRect = nodeDOM.querySelector('figure')?.getBoundingClientRect();
    const videoRect = nodeDOM.querySelector('iframe')?.getBoundingClientRect();
    if (!figureRect || !videoRect) return null;
    return videoRect.width > figureRect.width ? figureRect : videoRect;
  }

  private openEditDialog(data: VideoEditMenuData, callbacks: VideoEditMenuCallbacks) {
    this.menuController.openEdit(data, callbacks);
  }

  private getKeyBindings(schema: VideoSchema) {
    return {};
  }

  private deleteVideoNode(position: number) {
    this.menuController.closeEdit();
    this.api.videoService.delete(
      position,
      this.editorView.state,
      this.editorView.dispatch,
      this.editorView.focus,
    );
  }

  private changeSource({ isEditing, src }: VideoEditMenuResult, videoNode?: ProsemirrorNode) {
    this.menuController.closeEdit();
    if (isEditing) {
      this.api.videoService.updateSrc(
        videoNode!,
        src,
        this.editorView.state,
        this.editorView.dispatch,
      );
    } else {
      this.api.videoService.insert(src, this.editorView.state, this.editorView.dispatch);
    }
  }

  private createVideoNode(node: ProsemirrorNode, src: string) {
    return this.editorView.state.schema.nodes[NodeName.Video].create({ ...node.attrs, src });
  }
}
